import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createPick, analyzeMatch, getBookmakers, getLeagues } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Slider } from '../components/ui/slider';
import { Brain, Loader2, ArrowLeft, Crown, Gift, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function NewPick() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [bookmakers, setBookmakers] = useState([]);
  const [leagues, setLeagues] = useState({});

  // Pre-fill from URL params (when coming from Events page)
  const [form, setForm] = useState({
    sport: searchParams.get('sport') || 'football',
    league: searchParams.get('league') || 'la_liga',
    home_team: searchParams.get('home_team') || '',
    away_team: searchParams.get('away_team') || '',
    match_date: searchParams.get('match_date') ? new Date(searchParams.get('match_date')).toISOString().slice(0, 16) : '',
    prediction: '',
    odds: '',
    bookmaker: 'Bet365',
    stake: 5,
    confidence: 70,
    analysis: '',
    channel: 'free',
    bet_type: 'simple',
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [bookRes, leagueRes] = await Promise.all([
          getBookmakers(),
          getLeagues()
        ]);
        setBookmakers(bookRes.data);
        setLeagues(leagueRes.data);
      } catch (error) {
        console.error('Error fetching config:', error);
      }
    };
    fetchConfig();
  }, []);

  const handleAnalyze = async () => {
    if (!form.home_team || !form.away_team || !form.match_date) {
      toast.error('Completa equipo local, visitante y fecha');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await analyzeMatch({
        sport: form.sport,
        league: form.league,
        home_team: form.home_team,
        away_team: form.away_team,
        match_date: form.match_date,
      });
      setForm(prev => ({ ...prev, analysis: response.data.analysis }));
      toast.success('Análisis generado con IA');
    } catch (error) {
      console.error('Error analyzing:', error);
      toast.error('Error al generar análisis');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.home_team || !form.away_team || !form.prediction || !form.odds || !form.analysis) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      const pickData = {
        ...form,
        odds: parseFloat(form.odds),
        stake: form.stake,
        confidence: form.confidence,
      };
      
      await createPick(pickData);
      toast.success('Pronóstico creado');
      navigate('/picks');
    } catch (error) {
      console.error('Error creating pick:', error);
      toast.error('Error al crear pronóstico');
    } finally {
      setLoading(false);
    }
  };

  const currentLeagues = leagues[form.sport] || [];

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      
      <main className="pt-20 pb-12 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>

          <h1 className="font-heading text-3xl font-bold text-white mb-8">
            NUEVO <span className="text-[#00E599]">PRONÓSTICO</span>
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Channel Selection */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <Label className="text-zinc-300 mb-4 block">Canal de destino</Label>
              <RadioGroup
                value={form.channel}
                onValueChange={(v) => setForm(prev => ({ ...prev, channel: v }))}
                className="flex gap-4"
              >
                <label className={`flex-1 flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${form.channel === 'free' ? 'border-[#00E599] bg-[#00E599]/10' : 'border-zinc-700 hover:border-zinc-600'}`}>
                  <RadioGroupItem value="free" id="free" />
                  <Gift className="w-5 h-5 text-[#00E599]" />
                  <div>
                    <span className="font-medium text-white">Gratuito</span>
                    <p className="text-xs text-zinc-500">Para crear comunidad</p>
                  </div>
                </label>
                <label className={`flex-1 flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${form.channel === 'vip' ? 'border-[#EAB308] bg-[#EAB308]/10' : 'border-zinc-700 hover:border-zinc-600'}`}>
                  <RadioGroupItem value="vip" id="vip" />
                  <Crown className="w-5 h-5 text-[#EAB308]" />
                  <div>
                    <span className="font-medium text-white">VIP</span>
                    <p className="text-xs text-zinc-500">Pronóstico premium</p>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {/* Match Info */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 space-y-4">
              <h3 className="font-heading text-lg font-bold text-white mb-4">Información del Partido</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300 mb-2 block">Deporte</Label>
                  <Select value={form.sport} onValueChange={(v) => setForm(prev => ({ ...prev, sport: v, league: v === 'football' ? 'la_liga' : 'nba' }))}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="football">⚽ Fútbol</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300 mb-2 block">Liga</Label>
                  <Select value={form.league} onValueChange={(v) => setForm(prev => ({ ...prev, league: v }))}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {currentLeagues.map(l => (
                        <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300 mb-2 block">Equipo Local *</Label>
                  <Input
                    value={form.home_team}
                    onChange={(e) => setForm(prev => ({ ...prev, home_team: e.target.value }))}
                    className="input-dark"
                    placeholder="Real Madrid"
                    required
                    data-testid="input-home-team"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 mb-2 block">Equipo Visitante *</Label>
                  <Input
                    value={form.away_team}
                    onChange={(e) => setForm(prev => ({ ...prev, away_team: e.target.value }))}
                    className="input-dark"
                    placeholder="Barcelona"
                    required
                    data-testid="input-away-team"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 mb-2 block">Fecha y Hora del Partido *</Label>
                <Input
                  type="datetime-local"
                  value={form.match_date}
                  onChange={(e) => setForm(prev => ({ ...prev, match_date: e.target.value }))}
                  className="input-dark"
                  required
                  data-testid="input-match-date"
                />
              </div>
            </div>

            {/* Bet Info */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 space-y-4">
              <h3 className="font-heading text-lg font-bold text-white mb-4">Información de la Apuesta</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300 mb-2 block">Predicción *</Label>
                  <Input
                    value={form.prediction}
                    onChange={(e) => setForm(prev => ({ ...prev, prediction: e.target.value }))}
                    className="input-dark"
                    placeholder="Over 2.5 goles"
                    required
                    data-testid="input-prediction"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 mb-2 block">Cuota *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.odds}
                    onChange={(e) => setForm(prev => ({ ...prev, odds: e.target.value }))}
                    className="input-dark"
                    placeholder="1.85"
                    required
                    data-testid="input-odds"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300 mb-2 block">Casa de Apuestas</Label>
                  <Select value={form.bookmaker} onValueChange={(v) => setForm(prev => ({ ...prev, bookmaker: v }))}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {bookmakers.map(b => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300 mb-2 block">Stake: {form.stake}/10 unidades</Label>
                  <Slider
                    value={[form.stake]}
                    onValueChange={([v]) => setForm(prev => ({ ...prev, stake: v }))}
                    min={1}
                    max={10}
                    step={1}
                    className="mt-3"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 mb-2 block">Confianza: {form.confidence}%</Label>
                <Slider
                  value={[form.confidence]}
                  onValueChange={([v]) => setForm(prev => ({ ...prev, confidence: v }))}
                  min={1}
                  max={100}
                  step={1}
                  className="mt-3"
                />
              </div>
            </div>

            {/* Analysis */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold text-white">Análisis / Explicación *</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAnalyze}
                  disabled={analyzing}
                  className="border-[#6366F1] text-[#6366F1] hover:bg-[#6366F1]/10"
                  data-testid="analyze-btn"
                >
                  {analyzing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Brain className="w-4 h-4 mr-2" />
                  )}
                  Generar con IA
                </Button>
              </div>
              <Textarea
                value={form.analysis}
                onChange={(e) => setForm(prev => ({ ...prev, analysis: e.target.value }))}
                className="input-dark min-h-[120px]"
                placeholder="¿Por qué recomiendas esta apuesta? Explica brevemente..."
                required
                data-testid="input-analysis"
              />
              <p className="text-xs text-zinc-500 mt-2">
                Este texto se enviará junto con el pronóstico a Telegram
              </p>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" onClick={() => navigate(-1)} className="border-zinc-700">
                Cancelar
              </Button>
              <Button type="submit" className="btn-primary" disabled={loading} data-testid="submit-pick-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar Pronóstico
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
